import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { fetchWithAuth } from "@/lib/fetchWithAuth";
import { DEFINIR_SENHA_REDIRECT_KEY, clearSupabaseSessionFromStorage } from "@/lib/utils";
import { Eye, EyeOff, ArrowLeft, KeyRound, Mail, CheckCircle2, XCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { User, Session } from "@supabase/supabase-js";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CadastroParceiroAuth } from "@/components/Parceiros/CadastroParceiroAuth";

/** Países para login por telefone (código de discagem E.164). Brasil primeiro como padrão. */
const PHONE_COUNTRIES = [
  { code: "55", name: "Brasil", label: "Brasil (+55)" },
  { code: "1", name: "EUA/Canadá", label: "EUA/Canadá (+1)" },
  { code: "351", name: "Portugal", label: "Portugal (+351)" },
  { code: "54", name: "Argentina", label: "Argentina (+54)" },
  { code: "34", name: "Espanha", label: "Espanha (+34)" },
  { code: "39", name: "Itália", label: "Itália (+39)" },
  { code: "49", name: "Alemanha", label: "Alemanha (+49)" },
  { code: "33", name: "França", label: "França (+33)" },
  { code: "44", name: "Reino Unido", label: "Reino Unido (+44)" },
  { code: "52", name: "México", label: "México (+52)" },
  { code: "56", name: "Chile", label: "Chile (+56)" },
  { code: "57", name: "Colômbia", label: "Colômbia (+57)" },
] as const;

const DEFAULT_PHONE_COUNTRY = "55";

/** Converte número de telefone para formato E.164 usando o código do país selecionado */
function toE164Phone(value: string, countryCode: string): string {
  const digits = value.replace(/\D/g, "");
  const code = countryCode.replace(/\D/g, "");
  if (!digits.length) return "";
  if (digits.startsWith(code) && digits.length >= code.length + 8) {
    return `+${digits}`;
  }
  return `+${code}${digits}`;
}

/** E.164 mínimo para OTP: pelo menos 10 dígitos após o + (ex.: +5511999999999). Evita 422 por número inválido. */
function isE164ValidForOtp(phone: string): boolean {
  const digitsOnly = phone.replace(/\D/g, "");
  return digitsOnly.length >= 10 && digitsOnly.length <= 15;
}

const LANDING_URL = import.meta.env.VITE_LANDING_URL || "https://sun7energy.com.br";
const REMEMBER_CREDENTIALS_KEY = "sun7_link_remember_credentials";
/** Flag set before logout so /auth does not auto-redirect when session is still in storage (race). */
const LOGOUT_INTENT_KEY = "sun7_logout_intent";

function getRedirectAfterLogin(user: User): string {
  const temSenhaProvisoria = user.user_metadata?.senha_provisoria;
  const tipoCadastro = user.user_metadata?.tipo_cadastro;
  if (temSenhaProvisoria) {
    if (tipoCadastro === "vendedor") {
      return `/vendedor/definir-senha?token_hash=${user.id}&type=primeiro_acesso`;
    }
    return `/parceiro/definir-senha?token_hash=${user.id}&type=primeiro_acesso`;
  }
  return "/dashboard";
}

/** Usa o servidor como fonte da verdade para evitar JWT em cache com senha_provisoria desatualizada. */
async function getRedirectWithServerCheck(session: { user: User; access_token: string }): Promise<string> {
  const pathFromJwt = getRedirectAfterLogin(session.user);
  if (pathFromJwt !== "/dashboard") {
    const tipoCadastro = session.user.user_metadata?.tipo_cadastro;
    const apiPath = tipoCadastro === "vendedor"
      ? "/api/vendedores-auth/verificar-status"
      : "/api/parceiros-auth/verificar-status";
    try {
      const res = await fetchWithAuth(apiPath);
      if (res.status === 401) {
        // Token inválido/expirado (ex.: já definiu senha ou sessão antiga) → limpar e ficar em /auth
        clearSupabaseSessionFromStorage();
        window.location.href = "/auth";
        return "/auth";
      }
      if (res.ok) {
        const data = await res.json();
        if (data.senha_provisoria === false) {
          await supabase.auth.refreshSession();
          return "/dashboard";
        }
      }
    } catch {
      // Em caso de erro, mantém redirect para definir-senha
    }
  }
  return pathFromJwt;
}

const Auth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const [loginTab, setLoginTab] = useState<"email" | "telefone">("email");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPhone, setLoginPhone] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState(DEFAULT_PHONE_COUNTRY);
  const [loginPassword, setLoginPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showCadastroParceiroDialog, setShowCadastroParceiroDialog] = useState(false);
  // Login por telefone: fluxo OTP (Supabase não suporta phone+password no token grant em muitos projetos)
  const [phoneLoginStep, setPhoneLoginStep] = useState<"phone" | "otp">("phone");
  const [loginPhoneOtp, setLoginPhoneOtp] = useState("");

  // Estados do fluxo "Esqueceu senha?" (apenas via e-mail)
  const [showRecoveryDialog, setShowRecoveryDialog] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [recoveryStep, setRecoveryStep] = useState<"method" | "sent">("method");
  const [recoveryNewPassword, setRecoveryNewPassword] = useState("");
  const [recoveryConfirmPassword, setRecoveryConfirmPassword] = useState("");
  const [showRecoveryPassword, setShowRecoveryPassword] = useState(false);
  const [showRecoveryFromRedirect, setShowRecoveryFromRedirect] = useState(false);

  /** Validações da senha (recovery) - mesmas regras de DefinirSenha/primeiro acesso */
  const [validacoesRecovery, setValidacoesRecovery] = useState({
    tamanho: false,
    temLetra: false,
    temNumero: false,
    senhasIguais: false,
  });
  const senhaRecoveryValida =
    validacoesRecovery.tamanho &&
    validacoesRecovery.temLetra &&
    validacoesRecovery.temNumero &&
    validacoesRecovery.senhasIguais;

  useEffect(() => {
    setValidacoesRecovery({
      tamanho: recoveryNewPassword.length >= 6,
      temLetra: /[A-Za-z]/.test(recoveryNewPassword),
      temNumero: /\d/.test(recoveryNewPassword),
      senhasIguais: recoveryNewPassword !== "" && recoveryNewPassword === recoveryConfirmPassword,
    });
  }, [recoveryNewPassword, recoveryConfirmPassword]);

  /** Hash capturado na carga (antes do Supabase limpá-lo), para detectar fluxo de recuperação */
  const hashAtLoadRef = useRef(typeof window !== "undefined" ? window.location.hash : "");

  const whatsappNumber = "5548999737361";
  const whatsappUrl = `https://wa.me/${whatsappNumber}`;

  const prevLoginTab = useRef(loginTab);
  useEffect(() => {
    if (prevLoginTab.current !== "telefone" && loginTab === "telefone") {
      setPhoneLoginStep("phone");
    }
    prevLoginTab.current = loginTab;
  }, [loginTab]);

  // Carregar credenciais salvas ao montar a página (suporta formato antigo com "email" / "identifier")
  useEffect(() => {
    try {
      const saved = localStorage.getItem(REMEMBER_CREDENTIALS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as {
          mode?: "email" | "telefone";
          identifier?: string;
          email?: string;
          phone?: string;
          phoneCountryCode?: string;
          password?: string;
        };
        const id = parsed.identifier ?? parsed.email;
        if (parsed.mode === "telefone" || parsed.phone) {
          setLoginTab("telefone");
          setLoginPhone(parsed.phone ?? id ?? "");
          if (parsed.phoneCountryCode) setPhoneCountryCode(parsed.phoneCountryCode);
        } else if (id) {
          setLoginTab("email");
          setLoginEmail(id);
        }
        if (parsed.password) setLoginPassword(parsed.password);
        setRememberMe(true);
      }
    } catch {
      // ignora se o valor salvo for inválido
    }
  }, []);

  // Redirecionamento após definir senha (parceiro/vendedor): message + email na tela de login
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(DEFINIR_SENHA_REDIRECT_KEY);
      if (!raw) return;
      sessionStorage.removeItem(DEFINIR_SENHA_REDIRECT_KEY);
      const data = JSON.parse(raw) as { message?: string; email?: string };
      if (data.message) {
        toast({
          title: "Senha definida",
          description: data.message,
          duration: 6000,
        });
      }
      if (data.email) {
        setLoginTab("email");
        setLoginEmail(data.email);
      }
    } catch {
      // ignora se o valor for inválido
    }
  }, [toast]);

  useEffect(() => {
    const isRecoveryRedirect = () =>
      window.location.hash?.includes("type=recovery") || hashAtLoadRef.current?.includes("type=recovery");
    const isLogoutIntent = () => typeof sessionStorage !== "undefined" && sessionStorage.getItem(LOGOUT_INTENT_KEY) === "1";

    /** Ao redirecionar para dashboard, preservar a página de origem (ex.: F5 em /financeiro que caiu em /auth). */
    const resolveRedirectPath = (path: string): string => {
      if (path !== "/dashboard") return path;
      const from = location.state?.from as { pathname?: string; search?: string } | undefined;
      if (from?.pathname && from.pathname !== "/auth" && from.pathname !== "/") {
        return from.pathname + (from.search ?? "");
      }
      return path;
    };

    const maybeRedirect = (session: { user: User; access_token: string } | null) => {
      if (!session?.user || window.location.pathname !== "/auth") return;
      if (isRecoveryRedirect()) {
        setShowRecoveryFromRedirect(true);
        return;
      }
      if (isLogoutIntent()) {
        sessionStorage.removeItem(LOGOUT_INTENT_KEY);
        return;
      }
      getRedirectWithServerCheck(session).then((path) => navigate(resolveRedirectPath(path)));
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user && (event === "SIGNED_IN" || event === "PASSWORD_RECOVERY")) {
          if (event === "PASSWORD_RECOVERY" || isRecoveryRedirect()) {
            setShowRecoveryFromRedirect(true);
            return;
          }
          if (isLogoutIntent()) {
            sessionStorage.removeItem(LOGOUT_INTENT_KEY);
            return;
          }
          getRedirectWithServerCheck(session).then((path) => {
            setTimeout(() => navigate(resolveRedirectPath(path)), 0);
          });
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user && window.location.pathname === "/auth") {
        maybeRedirect(session);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, location]);

  // Detectar redirecionamento do link de recuperação por e-mail (type=recovery no hash)
  useEffect(() => {
    if (hashAtLoadRef.current?.includes("type=recovery") || window.location.hash?.includes("type=recovery")) {
      setShowRecoveryFromRedirect(true);
    }
  }, []);

  const handleRecoveryByEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!recoveryEmail.trim()) return;
    setIsLoading(true);
    try {
      const redirectTo = `${window.location.origin}/auth`;
      const { error } = await supabase.auth.resetPasswordForEmail(recoveryEmail.trim(), {
        redirectTo,
      });
      if (error) {
        const isRecoveryEmailError =
          (error as { code?: string }).code === "unexpected_failure" ||
          error.message.toLowerCase().includes("recovery email") ||
          error.message.toLowerCase().includes("sending recovery");
        const description = error.message.includes("rate limit")
          ? "Aguarde alguns minutos antes de tentar novamente."
          : isRecoveryEmailError
            ? "Não foi possível enviar o e-mail de recuperação. O administrador precisa configurar o envio de e-mails (SMTP) no Supabase. Tente novamente mais tarde ou entre em contato com o suporte."
            : error.message;
        toast({
          title: "Erro ao enviar link",
          description,
          variant: "destructive",
        });
        return;
      }
      setRecoveryStep("sent");
      toast({
        title: "Link enviado!",
        description: "Verifique seu e-mail e clique no link para redefinir sua senha.",
      });
    } catch (err) {
      toast({
        title: "Erro inesperado",
        description: "Tente novamente mais tarde.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecoverySetNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (recoveryNewPassword.length < 6) {
      toast({
        title: "Senha inválida",
        description: "A senha deve ter no mínimo 6 caracteres.",
        variant: "destructive",
      });
      return;
    }
    if (recoveryNewPassword !== recoveryConfirmPassword) {
      toast({
        title: "Senhas não coincidem",
        description: "Digite a mesma senha nos dois campos.",
        variant: "destructive",
      });
      return;
    }
    if (!/[A-Za-z]/.test(recoveryNewPassword) || !/\d/.test(recoveryNewPassword)) {
      toast({
        title: "Senha inválida",
        description: "A senha deve conter letras e números.",
        variant: "destructive",
      });
      return;
    }
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: recoveryNewPassword });
      if (error) throw error;
      toast({
        title: "Senha alterada com sucesso!",
        description: "Faça login com sua nova senha.",
      });
      await supabase.auth.signOut({ scope: 'local' });
      setShowRecoveryFromRedirect(false);
      setShowRecoveryDialog(false);
      setRecoveryStep("method");
      setRecoveryNewPassword("");
      setRecoveryConfirmPassword("");
      window.history.replaceState(null, "", window.location.pathname);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Erro ao atualizar senha.";
      toast({
        title: "Erro",
        description: msg,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const closeRecoveryDialog = () => {
    setShowRecoveryDialog(false);
    setRecoveryStep("method");
    setRecoveryEmail("");
  };

  const handleLoginWithEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginEmail.trim(),
        password: loginPassword,
      });
      if (error) {
        toast({
          title: "Erro de Login",
          description: error.message.includes("Invalid login credentials")
            ? "E-mail ou senha incorretos."
            : error.message,
          variant: "destructive",
        });
        return;
      }
      if (rememberMe) {
        localStorage.setItem(
          REMEMBER_CREDENTIALS_KEY,
          JSON.stringify({ mode: "email", email: loginEmail, password: loginPassword })
        );
      } else {
        localStorage.removeItem(REMEMBER_CREDENTIALS_KEY);
      }
      toast({
        title: "Login realizado com sucesso!",
        description: "Redirecionando para o dashboard...",
      });
      if (data?.user && data?.session) {
        const path = await getRedirectWithServerCheck(data.session);
        navigate(path);
      }
    } catch (error) {
      toast({
        title: "Erro inesperado",
        description: "Tente novamente em alguns instantes.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  /** Envia OTP por SMS para o telefone (login por telefone usa OTP, não senha, no Supabase). */
  const handlePhoneLoginSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    const phone = toE164Phone(loginPhone, phoneCountryCode);
    if (!phone) {
      toast({
        title: "Telefone inválido",
        description: "Digite um número de telefone válido.",
        variant: "destructive",
      });
      return;
    }
    if (!isE164ValidForOtp(phone)) {
      toast({
        title: "Número incompleto",
        description: "Informe o número com DDD/área (ex.: 11 99999-9999 para Brasil).",
        variant: "destructive",
      });
      return;
    }
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        phone,
        options: { channel: "sms" },
      });
      if (error) {
        if (import.meta.env.DEV) console.error("[Auth] signInWithOtp phone error:", error);
        let msg = error.message;
        if (error.message.includes("rate limit")) msg = "Aguarde alguns minutos e tente novamente.";
        else if (error.message.includes("signups not allowed") || error.message.includes("signup_disabled") || error.message.includes("otp_disabled") || (error as { code?: string }).code === "otp_disabled")
          msg = "Login por SMS está desativado ou este número não está cadastrado. Use e-mail ou entre em contato com o administrador.";
        else if (error.message.includes("User not found"))
          msg = "Este telefone não está cadastrado para login. Use e-mail ou entre em contato com o administrador.";
        else if (error.message.includes("Database error saving new user"))
          msg = "Erro ao criar usuário no banco. Verifique o trigger em auth.users → profiles (Auth e Postgres logs no Dashboard).";
        else if (msg.toLowerCase().includes("invalid") || msg.toLowerCase().includes("validate") || (error as { status?: number }).status === 422)
          msg = "Não foi possível enviar o SMS. Verifique o número (formato: +55 DDD 99999-9999) ou use um número já cadastrado.";
        toast({
          title: "Erro ao enviar código",
          description: msg,
          variant: "destructive",
        });
        return;
      }
      setPhoneLoginStep("otp");
      setLoginPhoneOtp("");
      toast({
        title: "Código enviado!",
        description: "Digite o código de 6 dígitos enviado por SMS.",
      });
    } catch (err: unknown) {
      const message =
        err && typeof err === "object" && "message" in err
          ? String((err as { message: unknown }).message)
          : "Tente novamente em alguns instantes.";
      toast({
        title: "Erro ao enviar código",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  /** Confirma o OTP e faz login (login por telefone via OTP). */
  const handlePhoneLoginVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginPhoneOtp || loginPhoneOtp.length !== 6) {
      toast({
        title: "Código inválido",
        description: "Digite o código de 6 dígitos recebido por SMS.",
        variant: "destructive",
      });
      return;
    }
    const phone = toE164Phone(loginPhone, phoneCountryCode);
    if (!phone) {
      toast({ title: "Telefone inválido", variant: "destructive" });
      return;
    }
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        phone,
        token: loginPhoneOtp,
        type: "sms",
      });
      if (error) {
        toast({
          title: "Código inválido",
          description: error.message,
          variant: "destructive",
        });
        return;
      }
      if (!data?.user || !data?.session) return;

      // Restringir na app: só permitir login de usuário já existente (não criado por este OTP).
      // Perfil criado há menos de 2 min = provável signup por OTP com número novo → bloquear.
      const { data: profile } = await supabase
        .from("profiles")
        .select("created_at")
        .eq("id", data.user.id)
        .single();
      if (!profile?.created_at) {
        await supabase.auth.signOut({ scope: "local" });
        toast({
          title: "Número não cadastrado",
          description: "Este telefone não está cadastrado na plataforma. Entre em contato com o administrador ou use e-mail para entrar.",
          variant: "destructive",
        });
        return;
      }
      const createdAt = new Date(profile.created_at).getTime();
      const twoMinutesAgo = Date.now() - 2 * 60 * 1000;
      if (createdAt >= twoMinutesAgo) {
        await supabase.auth.signOut({ scope: "local" });
        toast({
          title: "Número não cadastrado",
          description: "Este telefone não está cadastrado na plataforma. Entre em contato com o administrador ou use e-mail para entrar.",
          variant: "destructive",
        });
        return;
      }

      if (rememberMe) {
        localStorage.setItem(
          REMEMBER_CREDENTIALS_KEY,
          JSON.stringify({ mode: "telefone", phone: loginPhone, phoneCountryCode })
        );
      } else {
        localStorage.removeItem(REMEMBER_CREDENTIALS_KEY);
      }
      toast({
        title: "Login realizado com sucesso!",
        description: "Redirecionando para o dashboard...",
      });
      const path = await getRedirectWithServerCheck(data.session);
      navigate(path);
    } catch (err) {
      toast({
        title: "Erro inesperado",
        description: "Tente novamente em alguns instantes.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const goToLanding = () => {
    window.location.href = LANDING_URL;
  };

  // Overlay para redefinir senha quando usuário veio do link de e-mail
  if (showRecoveryFromRedirect && session?.user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
        <div className="fixed inset-0 -z-10" style={{ backgroundImage: `url(/lovable-uploads/a72a14fa-e841-4110-9233-f26a4d66d120.png)`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="fixed inset-0 -z-10 bg-sun7-blue-light/60 backdrop-blur-[1px]" />
        <Card className="w-full max-w-md bg-sun7-white/95 backdrop-blur-sm shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5" />
              Redefinir Senha
            </CardTitle>
            <CardDescription>
              Digite sua nova senha para acessar o Sun7 Link
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleRecoverySetNewPassword} className="space-y-4">
              <div className="space-y-2">
                <Label>Nova Senha</Label>
                <div className="relative">
                  <Input
                    type={showRecoveryPassword ? "text" : "password"}
                    placeholder="Digite sua senha"
                    value={recoveryNewPassword}
                    onChange={(e) => setRecoveryNewPassword(e.target.value)}
                    required
                    minLength={6}
                    autoComplete="new-password"
                  />
                  <Button type="button" variant="ghost" size="sm" className="absolute right-0 top-0 h-full" onClick={() => setShowRecoveryPassword(!showRecoveryPassword)}>
                    {showRecoveryPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Confirmar Senha</Label>
                <Input
                  type="password"
                  placeholder="Digite a senha novamente"
                  value={recoveryConfirmPassword}
                  onChange={(e) => setRecoveryConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </div>
              <div className="rounded-lg border bg-muted/50 p-4 space-y-2">
                <p className="text-sm font-medium">Requisitos da senha:</p>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    {validacoesRecovery.tamanho ? <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
                    <span className={`text-sm ${validacoesRecovery.tamanho ? "text-green-700 font-medium" : "text-muted-foreground"}`}>Mínimo 6 caracteres</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {validacoesRecovery.temLetra ? <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
                    <span className={`text-sm ${validacoesRecovery.temLetra ? "text-green-700 font-medium" : "text-muted-foreground"}`}>Pelo menos uma letra</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {validacoesRecovery.temNumero ? <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
                    <span className={`text-sm ${validacoesRecovery.temNumero ? "text-green-700 font-medium" : "text-muted-foreground"}`}>Pelo menos um número</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {validacoesRecovery.senhasIguais ? <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
                    <span className={`text-sm ${validacoesRecovery.senhasIguais ? "text-green-700 font-medium" : "text-muted-foreground"}`}>Senhas coincidem</span>
                  </div>
                </div>
              </div>
              <Button type="submit" className="w-full bg-gradient-accent" disabled={isLoading || !senhaRecoveryValida}>
                {isLoading ? "Salvando..." : "Redefinir Senha"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background fixo - não reflui ao alternar entre email/telefone */}
      <div
        className="fixed inset-0 -z-10"
        style={{
          backgroundImage: `url(/lovable-uploads/a72a14fa-e841-4110-9233-f26a4d66d120.png)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
      <div className="fixed inset-0 -z-10 bg-sun7-blue-light/60 backdrop-blur-[1px]" />

      <div className="w-full max-w-6xl relative z-10">
        <div className="mb-6 text-center">
          <Button
            variant="ghost"
            onClick={goToLanding}
            className="text-sun7-white hover:text-sun7-white hover:bg-white/20 mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar ao Site
          </Button>

          <div>
            <img
              src="/lovable-uploads/cb32df99-19a3-4507-a323-e635b65a6be9.png"
              alt="Sun7 Energy"
              className="h-[4.6rem] mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-sun7-white mb-2">Sun7 Link</h1>
            <p className="text-sun7-white/80">Acesse sua área exclusiva</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-sun7-white/10 backdrop-blur-sm rounded-lg p-6 border border-sun7-white/20 flex flex-col justify-between">
            <div>
              <h2 className="text-xl font-bold text-sun7-yellow mb-3">Torne-se um parceiro Sun7!</h2>
              <p className="text-sun7-white/90 text-sm leading-relaxed mb-4">
                Faça parte da equipe de parceiros da Sun7 e gerencie indicações e clientes através da plataforma exclusiva Sun7 Link com simplicidade e transparência. Faça seu cadastro agora mesmo clicando abaixo!
              </p>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-2">
              <p className="text-sun7-white/90 text-sm font-medium">
                Ainda não é um parceiro Sun7?{" "}
                <button
                  type="button"
                  onClick={() => setShowCadastroParceiroDialog(true)}
                  className="font-bold underline text-sun7-yellow hover:text-sun7-yellow/90 bg-transparent border-0 p-0 cursor-pointer inline"
                >
                  Cadastre-se
                </button>
              </p>
            </div>
          </div>

          <CadastroParceiroAuth
            open={showCadastroParceiroDialog}
            onClose={() => setShowCadastroParceiroDialog(false)}
            onSuccess={(message) => {
              toast({
                title: "Solicitação enviada",
                description: message,
                duration: 8000,
              });
            }}
          />

          {/* Dialog "Esqueceu senha?" - apenas via e-mail */}
          <Dialog open={showRecoveryDialog} onOpenChange={(open) => !open && closeRecoveryDialog()}>
            <DialogContent className="sm:max-w-md bg-sun7-white text-sun7-blue">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <KeyRound className="h-5 w-5" />
                  Recuperar acesso
                </DialogTitle>
                <DialogDescription>
                  Enviaremos um link para o e-mail cadastrado para redefinir sua senha
                </DialogDescription>
              </DialogHeader>

              {recoveryStep === "method" && (
                <form onSubmit={handleRecoveryByEmail} className="space-y-4 py-2">
                  <div>
                    <Label htmlFor="recovery-email">E-mail cadastrado</Label>
                    <Input
                      id="recovery-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={recoveryEmail}
                      onChange={(e) => setRecoveryEmail(e.target.value)}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? "Enviando..." : "Enviar link por e-mail"}
                  </Button>
                </form>
              )}

              {recoveryStep === "sent" && (
                <div className="space-y-4 py-2">
                  <p className="text-sm text-muted-foreground">
                    Enviamos um link para <strong>{recoveryEmail}</strong>. Acesse seu e-mail e clique no link para redefinir sua senha.
                  </p>
                  <Button variant="outline" onClick={closeRecoveryDialog} className="w-full">Fechar</Button>
                </div>
              )}

            </DialogContent>
          </Dialog>

          <div>
            <Tabs value={loginTab} onValueChange={(v) => setLoginTab(v as "email" | "telefone")} className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-sun7-white/10">
                <TabsTrigger
                  value="email"
                  className="text-sun7-white data-[state=active]:bg-sun7-white data-[state=active]:text-sun7-blue"
                >
                  E-mail
                </TabsTrigger>
                <TabsTrigger
                  value="telefone"
                  className="text-sun7-white data-[state=active]:bg-sun7-white data-[state=active]:text-sun7-blue"
                >
                  Telefone
                </TabsTrigger>
              </TabsList>

              <TabsContent value="email">
                <Card className="bg-sun7-white/95 backdrop-blur-sm min-h-[380px]">
                  <CardHeader>
                    <CardTitle>Login com E-mail</CardTitle>
                    <CardDescription>
                      Entre com seu e-mail e senha para acessar a plataforma
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleLoginWithEmail} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="login-email">E-mail</Label>
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="seu@email.com"
                          value={loginEmail}
                          onChange={(e) => setLoginEmail(e.target.value)}
                          required
                          autoComplete="username"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="login-password-email">Senha</Label>
                        <div className="relative">
                          <Input
                            id="login-password-email"
                            type={showPassword ? "text" : "password"}
                            placeholder="Sua senha"
                            value={loginPassword}
                            onChange={(e) => setLoginPassword(e.target.value)}
                            required
                            autoComplete="current-password"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="remember-me-email"
                            checked={rememberMe}
                            onCheckedChange={(checked) => setRememberMe(checked === true)}
                          />
                          <Label
                            htmlFor="remember-me-email"
                            className="text-sm font-normal cursor-pointer"
                          >
                            Lembrar usuário
                          </Label>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setRecoveryEmail(loginEmail);
                            setRecoveryStep("method");
                            setShowRecoveryDialog(true);
                          }}
                          className="text-sm text-sun7-blue hover:underline font-medium"
                        >
                          Esqueceu senha?
                        </button>
                      </div>

                      <Button
                        type="submit"
                        className="w-full bg-gradient-accent hover:shadow-glow"
                        disabled={isLoading}
                      >
                        {isLoading ? "Entrando..." : "Entrar"}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="telefone">
                <Card className="bg-sun7-white/95 backdrop-blur-sm min-h-[380px]">
                  <CardHeader>
                    <CardTitle>Login com Telefone</CardTitle>
                    <CardDescription>
                      {phoneLoginStep === "phone"
                        ? "Informe seu telefone para receber um código por SMS"
                        : "Digite o código de 6 dígitos enviado por SMS"}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {phoneLoginStep === "phone" ? (
                      <form onSubmit={handlePhoneLoginSendOtp} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="login-phone-country">País</Label>
                          <Select
                            value={phoneCountryCode}
                            onValueChange={setPhoneCountryCode}
                            required
                          >
                            <SelectTrigger id="login-phone-country" aria-label="País do telefone">
                              <SelectValue placeholder="Selecione o país" />
                            </SelectTrigger>
                            <SelectContent>
                              {PHONE_COUNTRIES.map((c) => (
                                <SelectItem key={c.code} value={c.code}>
                                  {c.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="login-phone">Telefone</Label>
                          <Input
                            id="login-phone"
                            type="tel"
                            placeholder={phoneCountryCode === "55" ? "(11) 99999-9999" : "Número com DDD"}
                            value={loginPhone}
                            onChange={(e) => setLoginPhone(e.target.value)}
                            required
                            autoComplete="tel-national"
                          />
                        </div>

                        <div className="flex items-center space-x-2">
                          <Switch
                            id="remember-me-phone"
                            checked={rememberMe}
                            onCheckedChange={(checked) => setRememberMe(checked === true)}
                          />
                          <Label htmlFor="remember-me-phone" className="text-sm font-normal cursor-pointer">
                            Lembrar usuário
                          </Label>
                        </div>

                        <Button
                          type="submit"
                          className="w-full bg-gradient-accent hover:shadow-glow"
                          disabled={isLoading}
                        >
                          {isLoading ? "Enviando código..." : "Enviar código"}
                        </Button>
                      </form>
                    ) : (
                      <form onSubmit={handlePhoneLoginVerifyOtp} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="login-phone-otp">Código SMS</Label>
                          <Input
                            id="login-phone-otp"
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            placeholder="000000"
                            maxLength={6}
                            value={loginPhoneOtp}
                            onChange={(e) => setLoginPhoneOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            className="text-center text-lg tracking-[0.4em] font-mono"
                          />
                          <p className="text-xs text-muted-foreground">
                            Código enviado para {phoneCountryCode === "55" ? "" : `+${phoneCountryCode} `}
                            {loginPhone}
                          </p>
                        </div>

                        <Button
                          type="submit"
                          className="w-full bg-gradient-accent hover:shadow-glow"
                          disabled={isLoading || loginPhoneOtp.length !== 6}
                        >
                          {isLoading ? "Entrando..." : "Entrar"}
                        </Button>

                        <Button
                          type="button"
                          variant="ghost"
                          className="w-full"
                          onClick={() => {
                            setPhoneLoginStep("phone");
                            setLoginPhoneOtp("");
                          }}
                        >
                          Usar outro número
                        </Button>

                        <button
                          type="button"
                          onClick={() => {
                            setRecoveryEmail("");
                            setRecoveryStep("method");
                            setShowRecoveryDialog(true);
                          }}
                          className="text-sm text-sun7-blue hover:underline font-medium w-full"
                        >
                          Esqueceu senha?
                        </button>
                      </form>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
